import StickyHeader from "@components/Collection/artist/StickyHeader";
import FollowButton from "@components/Collection/artist/FollowButton";
import ArtistShareButton from "@components/Collection/artist/ShareButton";
import LBox from "@components/LBox";
import LGradient from "@components/LGradient";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import LPressable from "@components/LPressable";
import BottomBarPadding from "@components/BottomBarPadding";
import { useArtistProfileSuspense } from "@hooks/trpc/useArtists";
import {
  router,
  Slot,
  useFocusEffect,
  useLocalSearchParams,
} from "expo-router";
import { ReactNode, useState } from "react";
import { Dimensions } from "react-native";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import { fonts, hexToRGB, hexToRGBWorklet, theme } from "@theme";
import { useTopBarHeight } from "@hooks/useTopBarHeight";
import { useScrollValue } from "@components/AnimatedStore";
import { useIsFocused } from "expo-router/react-navigation";
import LSuspense from "@components/LSuspense";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import { SentryScreenDisplay } from "@components/SentryScreenDisplay";
import {
  DEFAULT_FEATURED_ACCESS_CARD_ACCENT,
  FeaturedAccessCard,
  PROFILE_HERO_HEIGHT,
  ProfileHero,
  createFeaturedAccessCardEventDate,
  formatFeaturedAccessCardPrice,
  formatFeaturedAccessCardLocation,
} from "@libs/shared-ui/native";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
import { useGamificationSummary } from "@hooks/trpc/useGamificationSummary";
import { useAccessCheckoutQuote } from "@hooks/trpc/useAccessCheckoutQuote";
import { useAuthStore } from "@utils/firebaseAuth/auth";
import { storeMoneyMinor } from "@libs/platform-wire";
import { MUSIC_ACCESS_FLOW_COPY } from "@libs/shared-ui";

const BACKGROUND_OPACITY_Y_TRIGGER = PROFILE_HERO_HEIGHT;
const IMAGE_Y_TRIGGER = BACKGROUND_OPACITY_Y_TRIGGER - 20;
const DEFAULT_ARTIST_ELO = 500;
const CONTENT_TOP_FADE_HEIGHT = 128;
const CONTENT_TOP_FADE_LOCATIONS: [
  number,
  number,
  number,
  number,
  number,
  number,
] = [0, 0.3, 0.52, 0.72, 0.88, 1];

const ArtistProfileLayoutComp = ({ artistId }: { artistId: string }) => {
  const {
    query: { data: artist },
  } = useArtistProfileSuspense(artistId);
  const { data: gamificationSummary } = useGamificationSummary();
  const authStatus = useAuthStore((state) => state.status);
  const pinnedEvent =
    artist?.pinnedAccess?.type === "event" ? artist.pinnedAccess : null;
  const { data: pinnedAccessQuote } = useAccessCheckoutQuote(
    pinnedEvent
      ? {
          accessId: pinnedEvent.id,
          numberOfTickets: 1,
          useGems: true,
        }
      : null,
  );

  const screenHeight = Dimensions.get("screen").height;
  const [boxHeight, setBoxHeight] = useState(screenHeight);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  const scrollValue = useScrollValue();
  const y = useSharedValue(0);
  const isFocused = useIsFocused();
  // Keep the last slot content rendered while focused so it stays mounted
  // during the blur transition (instead of unmounting/reloading). Captured
  // during render via the store-previous-value pattern so we never read/write
  // a ref during render.
  const [cachedSlotContent, setCachedSlotContent] = useState<ReactNode>(
    isFocused ? <Slot /> : null,
  );

  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll(event) {
        if (isFocused) {
          scrollValue.set(event.contentOffset.y);
          y.value = event.contentOffset.y;
        }
      },
    },
    [isFocused],
  );

  const [prevFocused, setPrevFocused] = useState(isFocused);
  if (isFocused !== prevFocused) {
    setPrevFocused(isFocused);
    if (isFocused) {
      setCachedSlotContent(<Slot />);
    }
  }

  useFocusEffect(() => {
    // when navigating back to the screen, we want to update the scroll value again
    // e.g we opened a collection, the header's scroll value is 0, if our screen was showing the top bar title, we want to update the scroll value again
    // adding a frame delay ensures the collectionTitle is unmounted before we change the scroll value
    // otherwise the previous title (e.g. album title) will be shown again
    const timeout = setTimeout(() => {
      scrollValue.value = y.value;
    }, 16);
    return () => {
      clearTimeout(timeout);
      scrollValue.value = 0;
    };
  });

  const topBarHeight = useTopBarHeight();
  const artistArtworkHeight = BACKGROUND_OPACITY_Y_TRIGGER;

  // A style that returns a transform counts as changed on every run, and each
  // change commits a layout pass on the UI thread. Deriving the clamped scale
  // first stops the style running once the image is past its range.
  const imageScale = useDerivedValue(() =>
    interpolate(y.value, [0, IMAGE_Y_TRIGGER], [1, 1.2], Extrapolation.CLAMP),
  );

  const imageStyle = useAnimatedStyle(() => {
    return { transform: [{ scale: imageScale.value }] };
  });

  const backgroundStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      y.value,
      [0, BACKGROUND_OPACITY_Y_TRIGGER - 20],
      [0, 1],
      Extrapolation.CLAMP,
    );
    return {
      backgroundColor: hexToRGBWorklet(theme.colors.screenBg, opacity),
    };
  });

  if (!artist) {
    return <LScreen />;
  }

  // The profile never pins merch (the API leaves it out of every list).
  const pinnedAccess =
    artist.pinnedAccess?.type === "merch" ? null : artist.pinnedAccess;
  const featuredAccessCopy = pinnedAccess
    ? pinnedAccess.type === "event"
      ? {
          title: pinnedAccess.title,
          subtitle: formatFeaturedAccessCardLocation(
            pinnedAccess.city,
            pinnedAccess.country,
          ),
        }
      : {
          title: pinnedAccess.songs[0].title,
          subtitle:
            pinnedAccess.musicAccessType === "pre-release"
              ? "Pre-release"
              : "Unreleased",
        }
    : undefined;
  const featuredMusicArtworkUrl =
    pinnedAccess?.type === "music"
      ? pinnedAccess.songs[0].albums.at(0)?.image ?? pinnedAccess.artistImage
      : null;
  const featuredAccessLeadingVisual = pinnedAccess
    ? pinnedAccess.type === "event"
      ? createFeaturedAccessCardEventDate(pinnedAccess.startsAtLocal.date)
      : featuredMusicArtworkUrl
        ? {
            type: "exclusive-artwork" as const,
            artworkUrl: featuredMusicArtworkUrl,
          }
        : undefined
    : undefined;
  const artistElo =
    gamificationSummary?.artistStatuses.find(
      (status) => status.artistId === artistId,
    )?.elo ?? DEFAULT_ARTIST_ELO;
  const hasPinnedAccessDiscount =
    pinnedAccessQuote?.applied === true &&
    pinnedAccessQuote.appliedDiscountMinorUnits > 0;
  const featuredAccessAction = pinnedEvent
    ? {
        label: "Tickets",
        ...(pinnedEvent.price !== null
          ? {
              value: hasPinnedAccessDiscount
                ? storeMoneyMinor(
                    pinnedAccessQuote.amountDueMinorUnits,
                    pinnedAccessQuote.currency,
                  )
                : formatFeaturedAccessCardPrice(
                    pinnedEvent.price,
                    pinnedEvent.priceCurrency,
                  ),
            }
          : {}),
        ...(hasPinnedAccessDiscount
          ? {
              previousValue: formatFeaturedAccessCardPrice(
                pinnedAccessQuote.purchasePriceMinorUnits / 100,
                pinnedAccessQuote.currency,
              ),
            }
          : {}),
      }
    : pinnedAccess
      ? {
          // Members-only music is not unlocked: it is its club's members'
          // (LIS-1156).
          label:
            pinnedAccess.type === "music" &&
            pinnedAccess.membersOnly &&
            !pinnedAccess.unlocked
              ? MUSIC_ACCESS_FLOW_COPY.membersOnly
              : MUSIC_ACCESS_FLOW_COPY.unlock,
          locked: authStatus !== "logged-in",
        }
      : undefined;

  return (
    <>
      <SentryScreenDisplay ready />
      <LBox
        bottom={0}
        height={topBarHeight}
        left={0}
        position="absolute"
        right={0}
        style={backgroundStyle}
        top={0}
        zIndex={1}
      />
      <LBox flex={1} backgroundColor="screenBg">
        <LBox
          height={artistArtworkHeight}
          left={0}
          position="absolute"
          right={0}
          top={0}
        >
          <ProfileHero.Media imageUrl={artist.image} imageStyle={imageStyle} />
        </LBox>
        <Animated.ScrollView
          onLayout={(e) => {
            setBoxHeight(e.nativeEvent.layout.height);
          }}
          alwaysBounceVertical={false}
          bounces={false}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          stickyHeaderIndices={[1]}
          showsVerticalScrollIndicator={false}
        >
          <LBox height={artistArtworkHeight} width="100%">
            <LGradient
              bottom={0}
              colors={[
                theme.colors.transparent,
                theme.colors.transparent,
                hexToRGB(theme.colors.screenBg, 0.08),
                hexToRGB(theme.colors.screenBg, 0.28),
                hexToRGB(theme.colors.screenBg, 0.62),
                theme.colors.screenBg,
              ]}
              height={CONTENT_TOP_FADE_HEIGHT}
              left={0}
              locations={CONTENT_TOP_FADE_LOCATIONS}
              pointerEvents="none"
              position="absolute"
              right={0}
            />
            <ProfileHero.Content
              title={artist.name}
              style={{ bottom: 10 }}
              titleStyle={{ fontFamily: fonts.extraBold }}
            >
              <LBox
                alignItems="center"
                flexDirection="row"
                justifyContent="space-between"
                minHeight={36}
                width="100%"
              >
                <LBox alignItems="baseline" flexDirection="row" gap="s1">
                  <LText
                    color="white"
                    fontFamily={fonts.semiBold}
                    fontSize={14}
                    lineHeight={18}
                    opacity={0.82}
                    style={{
                      textShadowColor: "rgba(0, 0, 0, 0.72)",
                      textShadowOffset: { width: 0, height: 1 },
                      textShadowRadius: 6,
                    }}
                  >
                    ELO
                  </LText>
                  <LText
                    color="white"
                    fontFamily={fonts.semiBold}
                    fontSize={14}
                    lineHeight={18}
                    style={{
                      textShadowColor: "rgba(0, 0, 0, 0.72)",
                      textShadowOffset: { width: 0, height: 1 },
                      textShadowRadius: 6,
                    }}
                  >
                    {Math.round(artistElo).toLocaleString("en-GB")}
                  </LText>
                </LBox>
                <LBox alignItems="center" flexDirection="row" gap="s3">
                  <FollowButton artistId={artistId} />
                  <ArtistShareButton artist={artist} />
                </LBox>
              </LBox>
            </ProfileHero.Content>
          </LBox>
          <StickyHeader
            setStickyHeaderHeight={setStickyHeaderHeight}
            artistId={artistId}
            featuredAccess={
              pinnedAccess ? (
                <LBox width="100%" px="s2" mb="s3">
                  <LPressable
                    onPress={() => {
                      prefetchAccessDetailHeroImage(pinnedAccess);
                      router.push(`/access/${pinnedAccess.id}`);
                    }}
                    sentryEventName="ArtistProfile.FeaturedAccess"
                  >
                    <FeaturedAccessCard
                      background={
                        pinnedAccess.type === "event" ? "concert" : "song"
                      }
                      accentColor={DEFAULT_FEATURED_ACCESS_CARD_ACCENT}
                      action={featuredAccessAction}
                      copy={featuredAccessCopy}
                      leadingVisual={featuredAccessLeadingVisual}
                    />
                  </LPressable>
                </LBox>
              ) : null
            }
          />
          <LBox
            width="100%"
            backgroundColor="screenBg"
            gap="s2"
            minHeight={boxHeight - stickyHeaderHeight + 1}
          >
            {isFocused ? <Slot /> : cachedSlotContent}
            <BottomBarPadding />
          </LBox>
        </Animated.ScrollView>
      </LBox>
    </>
  );
};

const Layout = () => {
  const isFocused = useIsFocused();
  const artistIdFromPath = useNavigationStore((state) => {
    const path = state.currentPath;
    return path.includes("/artist/")
      ? path.split("/artist/").at(1)?.split("/").at(0)
      : undefined;
  });

  // Expo can retain the parent layout's params when pushing another artist
  // into its Slot. Use the active route for the focused layout so its header
  // and actions agree with the songs; preserve offscreen layouts for Back.
  const { artistId: paramArtistId } = useLocalSearchParams<{
    artistId: string;
  }>();

  const artistId = isFocused
    ? artistIdFromPath ?? paramArtistId
    : paramArtistId;

  return (
    <LScreen paddingHorizontal="zero">
      <LSuspense>
        <LBox width="100%" height="100%">
          <ArtistProfileLayoutComp artistId={artistId} />
        </LBox>
      </LSuspense>
    </LScreen>
  );
};

export default Layout;
